/*

   Hook to store table metadata in.

   Table functionality is divided into a number of render-only React
   components (Table, TableRow, etc), the table's actual contents
   (managed by you), and this hook, which stores the following types
   of metadata:

   - Current ordering information

   - Current page and other pagination

   - information Current search field and search string

   With some added functionality on top; e.g. the ordering information
   is translated into an "ordering string" that an API may (or may
   not) accept, and changing the search or ordering data resets the
   current page back to 1.

   *Nothing is done with this information*, it is up to you to use the
   parts you need to acquire the right data to currently show in the
   table. Some returned functions are meant to be passed as callbacks
   to rendering components.

   *Everything is optional*, you can choose to just not destructure
   the returned values that you don't need. Not every table needs
   ordering, search or pagination.

   Note that checkboxes are *not* in this hook, they are in useTableCheckboxes
   which is used directly by the Table component.

   USAGE

   // All of the destructured fields below are optional
   const {
     orderings,
     setOrdering,
     orderingString,
     page,
     pageSize,
     limit,
     offset,
     nextPage,
     previousPage,
     setPage,
     setPageSize,
     searchField,
     setSearchField,
     searchString,
     setSearchString
   } = useTableData({ ...options... });

   The returned fields are:

   - orderings: an object {[fieldname: string]: Ordering} where
   Ordering is "az", "za" or undefined.

   - setOrdering(fieldname, ordering): updates orderings. Currently
   there is always only a single ordering, not multiple, so any
   already there are deleted (could be changed later). Resets page
   to 1.

   - orderingString: a string of the form "fieldname,-otherfieldname"
   to send to e.g. the Lizard API (as there is currently only a
   single field in orderings, this will not include a comma, but it
   might later).

   - page: current page. Starts at 1

   - pageSize: current page size, starts at 10.

   - limit: synonym for pageSize.

   - offset: index of first element, computed with page and pageSize.

   - nextPage: function to increase page by 1.

   - previousPage: function to decrease page by 1, or undefined if page is already 1.

   - setPage: a function to directly set page.

   - setPageSize: a function to set page size. Sets page to 1 if the value is different from before.

   - searchField: the current search field. Initial value can be given as parameter to useTableData.

   - setSearchField: changes the search field. Sets page to 1 if the value is different from before.

   - setSearchString: changes the search string. Sets page to 1 if the value is different from before.

   Options is an optional object with all optional fields, with these meanings:

   - initialOrderings: default {}, the initial value for orderings above

   - initialSearchString: default "", the initial value for the search string

   - initialSearchField: default "" (none selected), the initial value for the search field

   - initialPage: default 1, the initial page

   - initialPageSize: default 10, the initial page size
 */

import { useCallback, useMemo } from "react";

import { SearchValue, UseSearchStateOptions, useSearchState } from "./use-search-state";

type Ordering = "az" | "za" | undefined;

export interface Orderings {
  [field: string]: Ordering;
}

type DefaultTableDataOptions = {
  initialOrderings: Orderings;
  initialSearchString: string;
  initialSearchField: string;
  initialPage: number;
  initialPageSize: number;
};

type SearchParamsTableDataOptions = {
  hasSearchParams: true;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  keyAddon: string;
};

export type PartialUseTableDataOptions =
  | Partial<DefaultTableDataOptions & { hasSearchParams: false }>
  | (Partial<DefaultTableDataOptions> & SearchParamsTableDataOptions);

export type UseTableDataOptions =
  | Required<DefaultTableDataOptions & { hasSearchParams: false }>
  | (Required<DefaultTableDataOptions> & SearchParamsTableDataOptions);

const DEFAULT_OPTIONS: UseTableDataOptions = {
  initialOrderings: {},
  initialSearchString: "",
  initialSearchField: "",
  initialPage: 1,
  initialPageSize: 20,
  hasSearchParams: false,
};

type SearchStateParams<T extends SearchValue> = [string, T, UseSearchStateOptions];

interface TableSearchStateParams {
  pageSearchParams: SearchStateParams<number>;
  pageSizeSearchParams: SearchStateParams<number>;
  orderingSearchParams: SearchStateParams<string>;
  searchFieldSearchParams: SearchStateParams<string>;
  searchStringSearchParams: SearchStateParams<string>;
}

export const getKeyWithAddon = (
  keyAddon: string,
  key: "page" | "pageSize" | "order" | "searchField" | "searchString"
) => `${keyAddon}_${key}`;

function buildTableSearchStateParams(options: UseTableDataOptions): TableSearchStateParams {
  const {
    initialOrderings,
    initialPage,
    initialPageSize,
    initialSearchString,
    initialSearchField,
  } = options;

  if (!options.hasSearchParams)
    return {
      pageSearchParams: ["", initialPage, {}],
      pageSizeSearchParams: ["", initialPageSize, {}],
      orderingSearchParams: ["", buildOrderingString(initialOrderings), {}],
      searchFieldSearchParams: ["", initialSearchField, {}],
      searchStringSearchParams: ["", initialSearchString, {}],
    };

  const { searchParams, setSearchParams, keyAddon } = options;
  const searchOptions: UseSearchStateOptions = {
    searchParams,
    setSearchParams,
    dontEverSaveInHistory: true,
  };
  return {
    pageSearchParams: [getKeyWithAddon(keyAddon, "page"), initialPage, searchOptions],
    pageSizeSearchParams: [getKeyWithAddon(keyAddon, "pageSize"), initialPageSize, searchOptions],
    orderingSearchParams: [
      getKeyWithAddon(keyAddon, "order"),
      buildOrderingString(initialOrderings),
      searchOptions,
    ],
    searchFieldSearchParams: [
      getKeyWithAddon(keyAddon, "searchField"),
      initialSearchField,
      searchOptions,
    ],
    searchStringSearchParams: [
      getKeyWithAddon(keyAddon, "searchString"),
      initialSearchString,
      searchOptions,
    ],
  };
}

function buildOrderingString(orderings: Orderings): string {
  return Object.entries(orderings)
    .map(([field, ordering]) => {
      if (ordering === "az") return field;
      if (ordering === "za") return `-${field}`;
      return null;
    })
    .filter(Boolean)
    .join(",");
}

function parseOrderingString(str: string): Orderings {
  if (!str) return {};
  return Object.fromEntries(
    str.split(",").map((part) => {
      const za = part.startsWith("-");
      return [za ? part.slice(1) : part, za ? "za" : "az"];
    })
  );
}

export function useTableData(partialOptions: PartialUseTableDataOptions = {}) {
  const options: UseTableDataOptions = { ...DEFAULT_OPTIONS, ...partialOptions };

  const {
    pageSearchParams,
    pageSizeSearchParams,
    orderingSearchParams,
    searchFieldSearchParams,
    searchStringSearchParams,
  } = buildTableSearchStateParams(options);

  const [page, setPage] = useSearchState(...pageSearchParams);
  const [pageSize, _setPageSize] = useSearchState(...pageSizeSearchParams);
  const [searchString, _setSearchString] = useSearchState(...searchStringSearchParams);
  const [searchField, _setSearchField] = useSearchState(...searchFieldSearchParams);
  const [orderingString, setOrderingString] = useSearchState(...orderingSearchParams);

  // Derive orderings object from the URL-synced ordering string so there is one source of truth.
  const orderings: Orderings = useMemo(() => parseOrderingString(orderingString), [orderingString]);

  const setOrdering = useCallback(
    (field: string, ordering: Ordering) => {
      // Currently the only implementation is to always order on a single column.
      setOrderingString(buildOrderingString({ [field]: ordering }));
    },
    [setOrderingString]
  );

  const setSearchField = useCallback(
    (field: string) => {
      if (field !== searchField) {
        _setSearchField(field);
        _setSearchString("");
        setPage(1);
      }
    },
    [searchField, _setSearchField, _setSearchString, setPage]
  );

  const setSearchString = useCallback(
    (search: string) => {
      if (search !== searchString) {
        _setSearchString(search);
        setPage(1);
      }
    },
    [searchString, _setSearchString, setPage]
  );

  const nextPage = useCallback(() => setPage(page + 1), [page, setPage]);
  const previousPage = useCallback(() => setPage(page - 1), [page, setPage]);
  const setPageSize = useCallback(
    (size: number) => {
      if (pageSize !== size) {
        _setPageSize(size, { dontSaveInHistory: true });
        setPage(1);
      }
    },
    [pageSize, _setPageSize, setPage]
  );

  return {
    orderings,
    setOrdering,
    orderingString,
    page,
    pageSize,
    limit: pageSize,
    offset: Math.max((page - 1) * pageSize, 0),
    nextPage,
    previousPage: page > 1 ? previousPage : undefined,
    setPage,
    setPageSize,
    searchField,
    setSearchField,
    searchString,
    setSearchString,
  };
}
